/**
 * PlacementTestEdit Component (Rebuilt)
 *
 * Clean, modern implementation for editing placement tests.
 * Features:
 * - Placement test metadata editing (name, summary, description, thumbnail, duration, dates)
 * - Question management with tabbed interface (reuses Test components)
 * - Support for all 9 question types including COMPREHENSION
 * - Program and difficulty level selection
 * - Media upload management
 * - Form validation before save
 * - High performance with React.memo and optimized re-renders
 */

import React, {useMemo} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {Button, Modal, Spin, Tabs, Typography} from 'antd';
import {PlacementTestMetadataForm} from './components/PlacementTestMetadataForm';
import {QuestionTabPanel} from '../Test/components/QuestionTabPanel';
import {usePlacementTestData} from './hooks/usePlacementTestData';
import {useComprehensionLogic} from '../Test/hooks/useComprehensionLogic';
import {useQuestionValidation} from '../Test/hooks/useQuestionValidation';
import type {QuestionWithNewFields} from '../Questions/QuestionTypes';

const {Title} = Typography;
const {TabPane} = Tabs;

/**
 * Main PlacementTestEdit component
 */
export const PlacementTestEdit: React.FC = () => {
  const history = useHistory();
  const params: {placementTestId: string} = useParams();

  // Data management hook (placement test specific)
  const {
    placementTestName,
    setPlacementTestName,
    placementTestDescription,
    setPlacementTestDescription,
    thumbnail,
    setThumbnail,
    media,
    placementTestAsset,
    setPlacementTestAsset,
    duration,
    setDuration,
    startDate,
    setStartDate,
    expirationDate,
    setExpirationDate,
    questions,
    setQuestions,
    activeQuestionIndex,
    setActiveQuestionIndex,
    addNewQuestion,
    deleteQuestion,
    programsList,
    isLoading,
    isSaving,
    savePlacementTest,
  } = usePlacementTestData(params.placementTestId);

  // Comprehension logic hook (SHARED - reuse from Test)
  const {
    selectedBlankIndex,
    setSelectedBlankIndex,
    handleTextChange,
    handleTextBlur,
    handleCorrectAnswerChange,
    handleChoiceValueChange,
    handleAddChoice,
  } = useComprehensionLogic();

  // Validation hook (SHARED - reuse from Test)
  const {validateAllQuestions} = useQuestionValidation();

  /**
   * Validates placement test metadata (custom validation)
   */
  const validatePlacementTestMetadata = () => {
    const errors: string[] = [];

    if (!placementTestName || placementTestName.trim() === '') {
      errors.push('Placement test name is required');
    }

    if (!placementTestDescription || placementTestDescription.trim() === '') {
      errors.push('Placement test description is required');
    }

    return {isValid: errors.length === 0, errors};
  };

  /**
   * Handles tab change (switch between questions)
   */
  const handleTabChange = (activeKey: string) => {
    const index = parseInt(activeKey);
    setActiveQuestionIndex(index);

    // Reset selected blank index for COMPREHENSION questions (always start with first blank)
    const question = questions[index];
    if (
      question &&
      question.type === 'COMPREHENSION' &&
      question.payload?.blanks?.length
    ) {
      setSelectedBlankIndex(0);
    }
  };

  /**
   * Handles tab edit action (add new question)
   */
  const handleTabEdit = (targetKey: any, action: 'add' | 'remove') => {
    if (action === 'add') {
      addNewQuestion();
    }
  };

  /**
   * Updates a specific question in the questions array
   */
  const handleQuestionUpdate = (updatedQuestion: QuestionWithNewFields) => {
    const updatedQuestions = [...questions];
    updatedQuestions[activeQuestionIndex] = updatedQuestion;
    setQuestions(updatedQuestions);
  };

  /**
   * Handles question deletion
   */
  const handleDeleteQuestion = async () => {
    await deleteQuestion(activeQuestionIndex);
  };

  /**
   * Handles text change for current question
   */
  const handleQuestionTextChange = (
    newText: string,
    isAIText: boolean = false,
  ) => {
    const updatedQuestions = handleTextChange(
      activeQuestionIndex,
      newText,
      questions,
      isAIText,
    );
    setQuestions(updatedQuestions);
  };

  /**
   * Handles text blur for current question
   */
  const handleQuestionTextBlur = () => {
    const updatedQuestions = handleTextBlur(activeQuestionIndex, questions);
    setQuestions(updatedQuestions);
  };

  /**
   * Handles COMPREHENSION choice value change (using payload.blanks structure)
   */
  const handleComprehensionChoiceChange = (
    blankIndex: number,
    choiceIndex: number,
    value: string,
  ) => {
    const updatedQuestions = handleChoiceValueChange(
      activeQuestionIndex,
      blankIndex,
      choiceIndex,
      value,
      questions,
    );
    setQuestions(updatedQuestions);
  };

  /**
   * Handles COMPREHENSION correct answer change (correctAnswer is a string, not an index)
   */
  const handleComprehensionCorrectAnswer = (
    blankIndex: number,
    correctAnswer: string,
  ) => {
    const updatedQuestions = handleCorrectAnswerChange(
      activeQuestionIndex,
      blankIndex,
      correctAnswer,
      questions,
    );
    setQuestions(updatedQuestions);
  };

  /**
   * Handles adding a new choice to COMPREHENSION question blank
   */
  const handleComprehensionAddNewChoice = (blankIndex: number) => {
    const updatedQuestions = handleAddChoice(
      activeQuestionIndex,
      blankIndex,
      questions,
    );
    setQuestions(updatedQuestions);
  };

  /**
   * Handles save button click with validation
   */
  const handleSave = async () => {
    try {
      // Validate placement test metadata
      const metadataValidation = validatePlacementTestMetadata();

      if (!metadataValidation.isValid) {
        window.alert(
          'Placement test metadata validation failed:\n' +
            metadataValidation.errors.join('\n'),
        );
        return;
      }

      // Validate all questions
      const questionsValidation = validateAllQuestions(questions);

      if (!questionsValidation.isValid) {
        window.alert(
          'Question validation failed:\n' +
            questionsValidation.errors.join('\n'),
        );
        return;
      }

      // Save placement test
      await savePlacementTest();

      // Navigate back to placement tests list
      history.push('/placement-tests');
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  // Memoize current question to prevent unnecessary re-renders
  const currentQuestion = useMemo(
    () => questions[activeQuestionIndex],
    [questions, activeQuestionIndex],
  );

  return (
    <>
      <div style={{padding: 10, width: '100vw'}}>
        <Title style={{textAlign: 'center'}}>Edit Placement Test</Title>

        {/* Placement Test Metadata Form */}
        <PlacementTestMetadataForm
          placementTestName={placementTestName}
          onPlacementTestNameChange={setPlacementTestName}
          placementTestDescription={placementTestDescription}
          onPlacementTestDescriptionChange={setPlacementTestDescription}
          thumbnail={thumbnail}
          onThumbnailChange={setThumbnail}
          media={media}
          placementTestAsset={placementTestAsset}
          onPlacementTestAssetChange={setPlacementTestAsset}
          duration={duration}
          onDurationChange={setDuration}
          startDate={startDate}
          onStartDateChange={setStartDate}
          expirationDate={expirationDate}
          onExpirationDateChange={setExpirationDate}
        />

        {/* Questions Tabs - SHARED component from Test module */}
        <Tabs
          type="editable-card"
          onChange={handleTabChange}
          activeKey={activeQuestionIndex.toString()}
          onEdit={handleTabEdit}>
          {questions?.map((question, index) => (
            <TabPane tab={`question_${index}`} key={index} closable={false}>
              {/* Only render the active tab's content for performance */}
              {index === activeQuestionIndex && (
                <QuestionTabPanel
                  question={question}
                  questionIndex={index}
                  testType={undefined} // PlacementTest doesn't have testType
                  programsList={programsList}
                  onQuestionUpdate={handleQuestionUpdate}
                  onDeleteQuestion={handleDeleteQuestion}
                  selectedBlankIndex={selectedBlankIndex}
                  onSelectedBlankIndexChange={setSelectedBlankIndex}
                  onTextChange={handleQuestionTextChange}
                  onTextBlur={handleQuestionTextBlur}
                  onComprehensionChoiceValueChange={
                    handleComprehensionChoiceChange
                  }
                  onComprehensionCorrectAnswerChange={
                    handleComprehensionCorrectAnswer
                  }
                  onComprehensionAddChoice={handleComprehensionAddNewChoice}
                  isDeleting={isSaving}
                />
              )}
            </TabPane>
          ))}
        </Tabs>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          loading={isSaving}
          disabled={isLoading || isSaving}
          style={{marginTop: '20px'}}>
          {isSaving ? 'Saving...' : 'Save Placement Test'}
        </Button>
      </div>

      {/* Loading Modal */}
      <Modal
        okButtonProps={{hidden: true}}
        cancelButtonProps={{hidden: true}}
        visible={isLoading}
        closable={true}>
        <div>
          <Spin size="large" style={{margin: 'auto', width: '100%'}} />
          <p style={{textAlign: 'center'}}>Loading...</p>
        </div>
      </Modal>
    </>
  );
};
